import { useState } from "react";
import { ArrowRight, Lock } from "lucide-react";
import { Container, Mono } from "@/components/site/primitives";

const API = process.env.REACT_APP_BACKEND_URL;

const messageFor = (status, detail = {}) => {
  if (status === 429) return `Too many attempts. Try again in ${Math.ceil((detail.retry_after || 900) / 60)} minutes.`;
  if (detail.remaining) return `Incorrect password. ${detail.remaining} attempt${detail.remaining === 1 ? "" : "s"} left.`;
  return "Incorrect password.";
};

export const LockScreen = ({ onUnlock }) => {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!value || busy) return;
    setBusy(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/gate/unlock`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: value }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) return onUnlock(data.token);
      setError(messageFor(r.status, data.detail));
      setValue("");
    } catch {
      setError("Could not reach the site. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="t-ink grain relative flex min-h-screen flex-col overflow-hidden bg-o-bg text-o-text" data-testid="lock-screen">
      <div aria-hidden className="blueprint blueprint-fade pointer-events-none absolute inset-0 opacity-80" />
      <Container className="relative flex flex-1 flex-col justify-center py-16">
        <div className="fade-up max-w-xl">
          <p className="font-display text-[15px] font-extrabold uppercase tracking-[0.24em]">
            Ordo <span className="font-medium text-o-dim">Intelligence</span>
          </p>
          <div className="mt-14 flex items-center gap-3">
            <Lock className="h-4 w-4 text-o-accent" />
            <Mono accent>Private preview</Mono>
          </div>
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-[-0.02em] sm:text-5xl">Enter the password to continue.</h1>
          <p className="mt-5 text-o-dim">Access is limited to invited reviewers.</p>
          <form onSubmit={submit} className="mt-10 flex flex-col gap-3 sm:flex-row" data-testid="lock-form">
            <label htmlFor="site-password" className="sr-only">Password</label>
            <input
              id="site-password"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Password"
              data-testid="lock-password-input"
              className="field-input flex-1 py-4 text-lg tracking-[0.3em]"
            />
            <button
              type="submit"
              disabled={busy || !value}
              data-testid="lock-submit-btn"
              className="group inline-flex items-center justify-center gap-3 bg-o-accent px-6 py-4 font-medium text-o-accent-ink transition-[filter,opacity] hover:brightness-110 disabled:opacity-50"
            >
              {busy ? "Checking" : "Enter"}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </form>
          <p role="alert" aria-live="assertive" className="mt-4 min-h-[1.5rem] text-sm text-o-proof" data-testid="lock-error">{error}</p>
        </div>
      </Container>
    </div>
  );
};
